import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { EASE } from "@/components/Reveal";

const fade = (delay) => ({
  initial: { opacity: 0, y: 28 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: EASE },
});

const micro = ["FOUNDER LED", "LINKEDIN FIRST", "WHITE-GLOVE STRATEGY", "8+ YEARS EXPERIENCE"];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yKnown = useTransform(scrollYProgress, [0, 1], [0, -140]);
  const yAuthority = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const yIdeas = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const yPresence = useTransform(scrollYProgress, [0, 1], [0, 180]);

  return (
    <section id="top" ref={ref} className="relative bg-[#09090B] overflow-hidden">
      <div className="hero-grain absolute inset-0 opacity-60" aria-hidden="true" />

      <div data-testid="hero-abstract-system" aria-hidden="true" className="absolute inset-0 pointer-events-none select-none">
        <motion.span style={{ y: yKnown }} className="font-serif-d font-black text-outline absolute -top-6 -left-4 text-[26vw] leading-none">
          KNOWN
        </motion.span>
        <motion.span style={{ y: yAuthority }} className="font-serif-d italic font-semibold text-outline absolute top-[30%] -right-10 text-[16vw] leading-none">
          AUTHORITY
        </motion.span>
        <motion.span style={{ y: yIdeas }} className="font-serif-d font-black text-outline absolute bottom-[16%] left-[4%] text-[18vw] leading-none">
          IDEAS
        </motion.span>
        <motion.span style={{ y: yPresence }} className="font-serif-d italic font-semibold text-outline absolute -bottom-8 right-[6%] text-[14vw] leading-none hidden md:block">
          PRESENCE
        </motion.span>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-40 sm:pt-52 pb-16 min-h-screen flex flex-col justify-between">
        <div>
          <motion.p
            {...fade(0)}
            data-testid="hero-eyebrow"
            className="text-xs uppercase tracking-[0.3em] text-[#CFE9EF]/80 flex items-center gap-3"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#118C4F] animate-pulse-dot" aria-hidden="true" />
            LinkedIn &amp; personal brand growth
          </motion.p>

          <motion.h1
            {...fade(0.15)}
            data-testid="hero-headline"
            className="font-serif-d font-black tracking-tight leading-[0.95] text-[13.5vw] sm:text-8xl lg:text-[7.5rem] text-[#F3F0E8] mt-8 max-w-6xl"
          >
            Become known for{" "}
            <span className="italic font-semibold text-[#CFE9EF]">what you know.</span>
          </motion.h1>

          <motion.p
            {...fade(0.3)}
            className="text-base sm:text-xl text-[#F3F0E8]/65 leading-relaxed max-w-2xl mt-8"
          >
            I bring white-glove LinkedIn strategy to founders, executives and
            businesses who want to be known for what they know — positioning,
            content, creative and growth, led personally by me.
          </motion.p>

          <motion.div {...fade(0.45)} className="flex flex-col sm:flex-row gap-4 mt-12">
            <a
              href="#contact"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center justify-center gap-2 bg-[#118C4F] hover:bg-[#F3F0E8] text-[#09090B] font-medium px-8 py-4 text-base transition-colors duration-300"
            >
              Book a discover call
              <ArrowRight size={17} strokeWidth={2.2} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#how-it-works"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center gap-2 border border-[#F3F0E8]/25 hover:border-[#F3F0E8]/70 text-[#F3F0E8] font-medium px-8 py-4 text-base transition-colors duration-300"
            >
              See how it works <ArrowDown size={17} strokeWidth={2.2} />
            </a>
          </motion.div>

          <motion.p
            {...fade(0.6)}
            data-testid="hero-credibility-line"
            className="mt-10 text-xs uppercase tracking-[0.25em] text-[#F3F0E8]/40"
          >
            LinkedIn <span className="text-[#118C4F]">·</span> Personal Brand <span className="text-[#118C4F]">·</span> Content <span className="text-[#118C4F]">·</span> Growth
          </motion.p>
        </div>

        <motion.div
          {...fade(0.75)}
          className="mt-16 pt-6 border-t border-[#F3F0E8]/10 flex flex-wrap gap-x-8 gap-y-2"
        >
          {micro.map((m) => (
            <span key={m} className="text-[10px] uppercase tracking-[0.3em] text-[#F3F0E8]/35">
              {m}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
